import {useFormik} from 'formik';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import * as Yup from 'yup';
import {HttpError} from '../../../../common/hooks/ApiClient';
import {useMutation} from '../../../../common/hooks/QueryHooks';
import {routes} from '../../../../routing/routes';
import {LoggedInEnterprise} from '../../common/Types';
import {useAuth} from '../../providers/AuthProvider';
import {useI18n} from '../../../../common/providers/I18nProvider';

const useRegister = () => {
	const [loading, setLoading] = useState(false);
	const [agree, setAgree] = useState(false);

	const {mutate} = useMutation<LoggedInEnterprise>('/enterpriser/auth/register');
	const navigate = useNavigate();
	const {login} = useAuth();
	const {i18n} = useI18n();
	const checkboxHandler = () => {
		setAgree(!agree);
	};

	const registerSchema = Yup.object().shape({
		email: Yup.string()
			.email(i18n('AUTH.REGISTER.VALIDATION.EMAIL.FORMAT'))
			.min(3, i18n('AUTH.REGISTER.VALIDATION.EMAIL.MIN', {val: 3}))
			.max(50, i18n('AUTH.REGISTER.VALIDATION.EMAIL.MAX', {val: 50}))
			.required(i18n('AUTH.REGISTER.VALIDATION.EMAIL.REQUIRED')),
		password: Yup.string()
			.min(6, i18n('AUTH.REGISTER.VALIDATION.PASSWORD.MIN', {val: 6}))
			.max(50, i18n('AUTH.REGISTER.VALIDATION.PASSWORD.MAX', {val: 50}))
			.required(i18n('AUTH.REGISTER.VALIDATION.PASSWORD.REQUIRED')),
	});

	const formik = useFormik({
		initialValues: {
			email: undefined,
			password: undefined,
			agree: undefined,
		},
		validationSchema: registerSchema,
		onSubmit: async (values, {setStatus, setSubmitting}) => {
			setLoading(true);
			try {
				const data = {email: values.email, password: values.password, agree: values.agree};
				const res = await mutate({data});

				login(res);
				navigate(routes.verifyProfile, {state: {email: values.email}});
			} catch (error) {
				if (error instanceof HttpError) {
					setStatus(error.message);
				}
			} finally {
				setSubmitting(false);
				setLoading(false);
			}
		},
	});

	const isEmailInvalid = formik.touched.email && formik.errors.email !== undefined;
	const isPasswordInvalid =
		formik.touched.password !== undefined && formik.touched.password && formik.errors.password !== undefined;

	const canNotSubmit = formik.isSubmitting || !formik.isValid || !formik.touched.email || agree === false;

	return {loading, formik, canNotSubmit, isEmailInvalid, isPasswordInvalid, checkboxHandler, agree};
};

export default useRegister;
